import dayjs from "dayjs";

/**
 * Backend date/time formatting, in one place.
 *
 * The API speaks two dialects. Most report views emit `DD-MM-YYYY` or
 * `DD-MM-YYYY hh:mm AM/PM`; a few emit ISO. Neither of the first two is
 * parseable by `dayjs(str)` — day-first strings either throw Invalid Date or,
 * worse, get read as month-first — so every report had grown its own parser.
 * Three of them split the string on '-' and reassembled it, which silently
 * dropped any time component and broke outright once the backend started
 * sending one.
 */

// DD-MM-YYYY, optionally followed by hh:mm[:ss] and an optional meridiem.
const DMY = /^(\d{2})-(\d{2})-(\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?\s*([AaPp][Mm])?)?/;

/** Any of the backend dialects -> a dayjs object, or null. */
export const parseBackendDate = (value) => {
    if (!value) return null;
    if (typeof value !== "string") {
        const d = dayjs(value);
        return d.isValid() ? d : null;
    }

    const m = value.match(DMY);
    if (!m) {
        const d = dayjs(value);                     // ISO, or anything dayjs knows
        return d.isValid() ? d : null;
    }

    const [, dd, mm, yyyy, hh, min, ss, meridiem] = m;
    if (!hh) return dayjs(`${yyyy}-${mm}-${dd}`);

    // Build an ISO string rather than handing dayjs a 12-hour clock: without the
    // customParseFormat plugin it would fall back to Date(), which reads
    // "03:45 PM" inconsistently across browsers.
    let hour = Number(hh);
    if (meridiem) {
        const isPm = meridiem.toUpperCase() === "PM";
        if (isPm && hour < 12) hour += 12;
        if (!isPm && hour === 12) hour = 0;
    }
    const d = dayjs(`${yyyy}-${mm}-${dd}T${String(hour).padStart(2, "0")}:${min}:${ss || "00"}`);
    return d.isValid() ? d : null;
};

/** True when the value actually carries a clock time. */
export const hasTime = (value) =>
    typeof value === "string" ? /\d{1,2}:\d{2}/.test(value) : value instanceof Date;

/**
 * Display a created/updated timestamp: "19 Aug 2026, 3:45 PM".
 *
 * Falls back to date-only when the value has no time rather than printing a
 * fictitious 12:00 AM — a filter boundary and a creation instant look the same
 * in JSON, and inventing midnight for the former reads as real data.
 */
export const formatDateTime = (value, fallback = "—") => {
    const d = parseBackendDate(value);
    if (!d) return fallback;
    return hasTime(value) ? d.format("DD MMM YYYY, h:mm A") : d.format("DD MMM YYYY");
};

/** Date only, whatever the input carried. For ranges and day buckets. */
export const formatDate = (value, fallback = "—") => {
    const d = parseBackendDate(value);
    return d ? d.format("DD MMM YYYY") : fallback;
};

/** Just the clock part, for two-line cells. Empty when there is no time. */
export const formatTime = (value, fallback = "") => {
    const d = parseBackendDate(value);
    return d && hasTime(value) ? d.format("h:mm A") : fallback;
};
